import * as React from "react";
import { Checkbox } from "./checkbox";

export interface IndeterminateCheckboxProps {
  /**
   * Whether the checkbox is checked
   */
  checked?: boolean;
  /**
   * Whether the checkbox is in an indeterminate state (partially selected)
   */
  indeterminate?: boolean;
  /**
   * Callback when the checkbox state changes.
   * Receives the new checked state as a boolean.
   */
  onChange?: (checked: boolean) => void;
  /**
   * Test ID for testing purposes
   */
  testId?: string;
  /**
   * Additional props to pass to the underlying checkbox
   */
  [key: string]: any;
}

/**
 * IndeterminateCheckbox is a wrapper around the Checkbox component
 * that provides a simpler interface for use with TanStack Table and other
 * row selection scenarios.
 *
 * It supports:
 * - Checked state (boolean)
 * - Indeterminate state (for "select all" with partial selection)
 * - Ref forwarding for TanStack Table compatibility
 * - Simplified onChange callback (boolean instead of Radix's CheckedState)
 *
 * @example
 * // In a DataTable header
 * <IndeterminateCheckbox
 *   checked={table.getIsAllRowsSelected()}
 *   indeterminate={table.getIsSomeRowsSelected()}
 *   onChange={table.getToggleAllRowsSelectedHandler()}
 * />
 *
 * @example
 * // In a DataTable row
 * <IndeterminateCheckbox
 *   checked={row.getIsSelected()}
 *   indeterminate={row.getIsSomeSelected()}
 *   disabled={!row.getCanSelect()}
 *   onChange={row.getToggleSelectedHandler()}
 * />
 */
const IndeterminateCheckbox = React.forwardRef<
  HTMLButtonElement,
  IndeterminateCheckboxProps
>(
  (
    { checked = false, indeterminate = false, onChange, testId, ...props },
    ref,
  ) => {
    // Handle the onChange to convert Radix's CheckedState to boolean
    const handleCheckedChange = React.useCallback(
      (checkedState: boolean | "indeterminate") => {
        // When indeterminate, clicking should check it
        // When checked, clicking should uncheck it
        // When unchecked, clicking should check it
        if (onChange) {
          onChange(checkedState === true);
        }
      },
      [onChange],
    );

    return (
      <Checkbox
        ref={ref}
        checked={checked}
        indeterminate={indeterminate}
        onCheckedChange={handleCheckedChange}
        data-testid={testId}
        {...props}
      />
    );
  },
);

IndeterminateCheckbox.displayName = "IndeterminateCheckbox";

export { IndeterminateCheckbox };
